/**
 * @file Hook wrapping the `getConfiguredProducts` callable. Consumer:
 * `useSignifyConfigurationSidebar`, which opens a configuration session for a configurable product.
 * `execute` rethrows the callable's own error, so this hook adds no message of its own and the
 * caller sees the `HttpsError` intact; `error` stays populated for the sidebar's banner.
 */

import { useCallback } from 'react';
import { SignifyConfiguredProductsResponse, SIGNIFY_CONFIG } from '#types/SignifyApi.types';
import { getConfiguredProducts } from '../services/signifyApiService';
import { useSignifyApi } from './useSignifyApi';

export const useSignifyConfiguredProducts = () => {
  const api = useSignifyApi<SignifyConfiguredProductsResponse>();

  const createConfiguredProducts = useCallback(
    async (
      customerId: string,
      distributorId: string,
      productId: string,
      fields: 'BASIC' | 'DEFAULT' | 'FULL' = SIGNIFY_CONFIG.defaultFields,
    ): Promise<SignifyConfiguredProductsResponse> =>
      api.execute(() => getConfiguredProducts(customerId, distributorId, productId, fields)),
    [api],
  );

  return {
    loading: api.loading,
    error: api.error,
    configuredProductsData: api.data,
    createConfiguredProducts,
    clearConfiguredProducts: api.clear,
  };
};
